import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static get targets() {
    return [
      "searchInput",
      "dataCount",
      "tag",
      "tagTemplate",
      "tagsList",
      "fieldLoader",
      "includeContinentValues",
      "excludeContinentValues",
      "includeBusinessRegionValues",
      "excludeBusinessRegionValues",
      "includeCountryValues",
      "excludeCountryValues",
      "includeStateValues",
      "excludeStateValues",
      "includeCityValues",
      "excludeCityValues",
    ]
  }

  static get values() {
    return { name: String }
  }

  // Setup the selectize element and add the already selected include and exclude values as tags.
  connect() {
    this._setUpSelectizeElement()
    this._setUpTags(this.includeContinentValuesTarget, this.excludeContinentValuesTarget, "continent")
    this._setUpTags(this.includeBusinessRegionValuesTarget, this.excludeBusinessRegionValuesTarget, "business_region")
    this._setUpTags(this.includeCountryValuesTarget, this.excludeCountryValuesTarget, "country")
    this._setUpTags(this.includeStateValuesTarget, this.excludeStateValuesTarget, "state")
    this._setUpTags(this.includeCityValuesTarget, this.excludeCityValuesTarget, "city")
    this._refreshDataCount()
    this._refreshSelectize()
  }

  // Clean up selectize
  disconnect() {
    this.selectizeElement?.destroy()
  }

  clear() {
    Array.from(this.includeContinentValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "continent", true)
    })

    Array.from(this.excludeContinentValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "continent", false)
    })

    Array.from(this.includeBusinessRegionValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "business_region", true)
    })

    Array.from(this.excludeBusinessRegionValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "business_region", false)
    })

    Array.from(this.includeCountryValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "country", true)
    })

    Array.from(this.excludeCountryValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "country", false)
    })

    Array.from(this.includeStateValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "state", true)
    })

    Array.from(this.excludeStateValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "state", false)
    })

    Array.from(this.includeCityValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "city", true)
    })

    Array.from(this.excludeCityValuesTarget.selectedOptions).forEach((option) => {
      this._removeTag(option.value, "city", false)
    })

    this._refreshDataCount()
    this._refreshSelectize()
    this._dispatchFilterEvent()
  }

  // Flip the include/exclude value of the given tag
  changeTag({ detail: { tagValue, included, dataset } }) {
    let tagText = this._removeTag(tagValue, dataset.type, !included)
    this._addTag(tagValue, tagText, dataset.type, included)
    this._addTagToSelect(tagValue, tagText, dataset.type, included)
    this._dispatchFilterEvent()
  }

  // Remove the tag from the list and add it back to the selectize element
  removeTag({ detail: { tagValue, included, dataset } }) {
    this._removeTag(tagValue, dataset.type, included)
    this._dispatchFilterEvent()
  }

  // Setup selectize to add a tag when selecting a value and then remove it from the selectize element.
  _setUpSelectizeElement() {
    this.runningRequests = 0

    this.selectizeElement = $(this.searchInputTarget).selectize({
      valueField: "value",
      labelField: "label",
      searchField: "label",
      optgroupField: "type",
      optgroups: [
        { value: "continent", label: "Continent" },
        { value: "business_region", label: "Business region" },
        { value: "country", label: "Country" },
        { value: "state", label: "State" },
        { value: "city", label: "City" },
      ],
      persist: false,
      create: false,
      copyClassesToDropdown: false,
      sortField: [{ field: "label" }],
      load: (query, callback) => {
        if (!query.length) {
          return callback()
        }

        // Show the spinner
        this.runningRequests += 1
        this.fieldLoaderTarget.classList.remove("d-none")

        fetch(`/locations?query=${encodeURIComponent(query)}`, {
          method: "GET",
          headers: { Accept: "application/json" },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            return response.json()
          })
          .then((data) => {
            data = data.map((item) => ({
              ...item,
              value: `${item.type}-${item.value}`,
            }))

            callback(data)
          })
          .catch((error) => {
            callback()
            console.error(error)
          })
          .finally(() => {
            this.runningRequests -= 1

            if (this.runningRequests <= 0) {
              // Hide the spinner, but only if there are no pending requests
              this.fieldLoaderTarget.classList.add("d-none")
            }
          })
      },
      onItemAdd: (compositeValue) => {
        // Extract the original type and value from the composite value
        const [, type, value] = compositeValue.match(/^([^-]+)-(.+)$/)
        let option = this.selectizeElement.options[compositeValue]

        // Check if this value already exists in either include or exclude select
        const includeSelect = this._getSelectElement(type, true)
        const excludeSelect = this._getSelectElement(type, false)
        const alreadyExists =
          Array.from(includeSelect.options).some((opt) => opt.value === value) ||
          Array.from(excludeSelect.options).some((opt) => opt.value === value)

        if (alreadyExists) {
          // Value already exists, just refresh selectize and return
          this._refreshSelectize()
          return
        }

        this._addTag(value, option.label, type)
        this._addTagToSelect(value, option.label, type, true)
        this._refreshSelectize()
        this._dispatchFilterEvent()
      },
    })[0].selectize
  }

  // Below method is not using _addTag because doing multiple DOM manipulations is not efficient.
  _setUpTags(includeValuesSelect, excludeValuesSelect, type) {
    // Combine include and exclude selected options into one array, and sort it alphabetically
    let combinedOptions = Array.from(includeValuesSelect.selectedOptions)
      .concat(Array.from(excludeValuesSelect.selectedOptions))
      .sort((a, b) => a.innerText.localeCompare(b.innerText))

    let documentFragment = document.createDocumentFragment()
    combinedOptions.forEach((option) => {
      // Determine if the option is from includeValuesTarget or excludeValuesTarget
      const isIncluded = Array.from(includeValuesSelect.selectedOptions).includes(option)
      const tagNode = this._createTagNode(option.innerText, option.value, type, isIncluded)
      documentFragment.appendChild(tagNode)
      this.selectizeElement.removeOption(option.value)
    })

    this.tagsListTarget.appendChild(documentFragment)
  }

  // This creates a tag using the HTML template and removes it from the selectize element.
  _addTag(value, label, type, included = true) {
    let tagNode = this._createTagNode(label, value, type, included)

    this.tagsListTarget.appendChild(tagNode)

    const tagsArray = Array.from(this.tagsListTarget.children)
    tagsArray.sort((a, b) => a.getAttribute("data-label").localeCompare(b.getAttribute("data-label")))
    tagsArray.forEach((element) => this.tagsListTarget.appendChild(element))

    this.selectizeElement.removeOption(value)
  }

  _createTagNode(label, value, type, included) {
    let tagHtml = this.tagTemplateTarget.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, label)
    tagHtml = tagHtml.replace(/%VALUE%/g, value)
    tagHtml = tagHtml.replace(/%TYPE%/g, type)
    tagHtml = tagHtml.replace(/%INCLUDED%/g, included)

    // Show tooltip for long label as they are truncated
    if (label.length > 22) {
      tagHtml = tagHtml.replace(/%TOOLTIP%/g, label)
    } else {
      tagHtml = tagHtml.replace(/%TOOLTIP%/g, "")
    }

    let tagNode = document.createElement("div")
    tagNode.innerHTML = tagHtml
    return tagNode.firstElementChild
  }

  // Refresh the data-count attribute
  _refreshDataCount() {
    if (!this.hasDataCountTarget) {
      return
    }

    let selectedOptions =
      this.includeContinentValuesTarget.selectedOptions.length +
      this.excludeContinentValuesTarget.selectedOptions.length +
      this.includeBusinessRegionValuesTarget.selectedOptions.length +
      this.excludeBusinessRegionValuesTarget.selectedOptions.length +
      this.includeCountryValuesTarget.selectedOptions.length +
      this.excludeCountryValuesTarget.selectedOptions.length +
      this.includeStateValuesTarget.selectedOptions.length +
      this.excludeStateValuesTarget.selectedOptions.length +
      this.includeCityValuesTarget.selectedOptions.length +
      this.excludeCityValuesTarget.selectedOptions.length

    if (selectedOptions > 0) {
      this.dataCountTarget.dataset.count = selectedOptions
    } else {
      delete this.dataCountTarget.dataset.count
    }
  }

  // Refresh selectize, clear the selected values and close it
  _refreshSelectize() {
    this.selectizeElement.refreshOptions(false)
    this.selectizeElement.clear()
    this.selectizeElement.close()
  }

  // Remove the tag from the list and the related select element.
  _removeTag(tagValue, type, included) {
    const selectElement = this._getSelectElement(type, included)
    const index = Array.from(selectElement.options).findIndex((option) => {
      return option.value === tagValue
    })
    const tagText = selectElement.options[index].innerText

    this.tagTargets.forEach((tag) => {
      if (tag.dataset.value === tagValue && tag.dataset.type === type) {
        tag.remove()
      }
    })

    selectElement.options.remove(index)
    this._refreshDataCount()

    return tagText
  }

  // Add the tag value and label to the right select element (included or excluded values)
  _addTagToSelect(value, label, type, included) {
    let option = new Option(label, value, true, true)
    let selectElement = this._getSelectElement(type, included)

    if (selectElement) {
      selectElement.options.add(option)
    }

    this._refreshDataCount()
  }

  // Dispatch the filter event, this can be used with other elements to apply the filters.
  _dispatchFilterEvent() {
    this.dispatch("filters", {
      detail: {
        filters: [
          {
            name: `${this.nameValue}_continent_included`,
            values: this._selectedValues(this.includeContinentValuesTarget),
          },
          {
            name: `${this.nameValue}_continent_excluded`,
            values: this._selectedValues(this.excludeContinentValuesTarget),
          },
          {
            name: `${this.nameValue}_business_region_included`,
            values: this._selectedValues(this.includeBusinessRegionValuesTarget),
          },
          {
            name: `${this.nameValue}_business_region_excluded`,
            values: this._selectedValues(this.excludeBusinessRegionValuesTarget),
          },
          { name: `${this.nameValue}_country_included`, values: this._selectedValues(this.includeCountryValuesTarget) },
          { name: `${this.nameValue}_country_excluded`, values: this._selectedValues(this.excludeCountryValuesTarget) },
          { name: `${this.nameValue}_state_included`, values: this._selectedValues(this.includeStateValuesTarget) },
          { name: `${this.nameValue}_state_excluded`, values: this._selectedValues(this.excludeStateValuesTarget) },
          { name: `${this.nameValue}_city_included`, values: this._selectedValues(this.includeCityValuesTarget) },
          { name: `${this.nameValue}_city_excluded`, values: this._selectedValues(this.excludeCityValuesTarget) },
        ],
      },
    })
  }

  _getSelectElement(type, included) {
    let selectElement

    switch (type) {
      case "continent":
        selectElement = included ? this.includeContinentValuesTarget : this.excludeContinentValuesTarget
        break
      case "business_region":
        selectElement = included ? this.includeBusinessRegionValuesTarget : this.excludeBusinessRegionValuesTarget
        break
      case "country":
        selectElement = included ? this.includeCountryValuesTarget : this.excludeCountryValuesTarget
        break
      case "state":
        selectElement = included ? this.includeStateValuesTarget : this.excludeStateValuesTarget
        break
      case "city":
        selectElement = included ? this.includeCityValuesTarget : this.excludeCityValuesTarget
        break
    }

    return selectElement
  }

  // Utility method to get all values from a select element.
  _selectedValues(selectElement) {
    return Array.from(selectElement.selectedOptions).map((option) => option.value)
  }
}
